import React, { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  GraduationCap,
  Users,
  CheckCircle2,
  XCircle,
  Award,
  TrendingUp,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import { StatCard } from '../../../shared/components/cards/StatCard';
import type { StudentRecord, SubjectResult } from '../types';
import type { ViewMode, MetricMode, ClassGroup, EntityRow } from '../components/classification/types';
import { BASE_GROUPS, STREAM_GROUP, METRIC_MODES } from '../components/classification/constants';
import Chip from '../components/classification/Chip';
import ViewModeToggle from '../components/classification/ViewModeToggle';
import SectionLabel from '../components/classification/SectionLabel';
import EntityBarChart from '../components/classification/EntityBarChart';
import SubjectPerformanceChart from '../components/SubjectPerformanceChart';
import ResultYearDropdown from '../components/ResultYearDropdown';
import {
  useGetResultKvDetailsQuery,
  useGetResultKvStudentResultDetailsQuery,
  type ResultKvDetailsComparisonItem,
  type ResultKvDetailsSubjectItem,
} from '../services/api';

const tabs = [
  { id: 'class10', label: 'Class 10', icon: BookOpen },
  { id: 'class12', label: 'Class 12', icon: GraduationCap },
] as const;

type TabKey = typeof tabs[number]['id'];
type SortKey = 'name' | 'totalMarks' | 'result';

const sanitizeLabel = (value?: string | number | null): string => {
  const label = String(value ?? '').trim();
  if (!label || label.toLowerCase() === 'nan') {
    return '';
  }

  return label;
};

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const mapKvCompToRows = (
  rows: ResultKvDetailsComparisonItem[] = [],
  resolveLabel: (item: ResultKvDetailsComparisonItem) => string | undefined,
): EntityRow[] =>
  rows.flatMap((item) => {
    const name = sanitizeLabel(resolveLabel(item));
    if (!name) return [];
    return [{
      name,
      passPercentage: toNumber(item.passPercentage),
      performanceIndex: toNumber(item.performanceIndex),
      totalRegistered: toNumber(item.totalRegistered),
      totalAppeared: toNumber(item.totalAppeared ?? item.totalRegistered),
      totalPassed: toNumber(item.passed),
      totalCompartment: toNumber(item.compartment ?? 0),
      totalAbsent: toNumber(item.absent ?? 0),
    }];
  }).filter((r) => r.totalAppeared > 0);

const mapRowsToSubjects = (
  rows: ResultKvDetailsSubjectItem[] = [],
  resolveLabel: (item: ResultKvDetailsSubjectItem) => string | undefined,
): SubjectResult[] =>
  rows.flatMap((item) => {
    const subject = sanitizeLabel(resolveLabel(item));
    if (!subject) {
      return [];
    }

    return [
      {
        subject,
        totalAppeared: toNumber(item.appearedStudents),
        performanceIndex: toNumber(item.performanceIndex) || toNumber(item.avgMarks),
        passPercentage: toNumber(item.passPercentage),
        grades: { A1: 0, A2: 0, B1: 0, B2: 0, C1: 0, C2: 0, D1: 0, D2: 0, E: 0 },
      },
    ];
  });

export default function SchoolDrillDown() {
  const { regionCode, kvCode } = useParams<{ regionCode: string; kvCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = (searchParams.get('class') as TabKey) || 'class10';
  const selectedYearParam = Number(searchParams.get('year') || '2025');
  const selectedYear = Number.isFinite(selectedYearParam) ? selectedYearParam : 2025;
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('totalMarks');
  const [sortAsc, setSortAsc] = useState(false);

  // Filter state
  const [viewMode, setViewMode] = useState<ViewMode>('graphical');
  const [selectedGroup, setSelectedGroup] = useState<ClassGroup>('overall');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [metricMode, setMetricMode] = useState<MetricMode>('overall');

  const grade = activeTab === 'class10' ? 10 : 12;
  const decodedRegion = decodeURIComponent(regionCode || '').trim();

  const EXCLUDED_GROUPS = new Set(['location', 'cwsn', 'pmshri', 'sponsoredby', 'sector']);
  const allGroups = useMemo(
    () => {
      const filtered = BASE_GROUPS.filter((g) => !EXCLUDED_GROUPS.has(g.id));
      return grade === 12 ? [...filtered, STREAM_GROUP] : filtered;
    },
    [grade], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const activeGroupDef = allGroups.find((g) => g.id === selectedGroup) ?? allGroups[0];
  const metric: 'passPercentage' | 'performanceIndex' = metricMode === 'pi' ? 'performanceIndex' : 'passPercentage';

  function handleGroupChange(g: ClassGroup) {
    const grp = allGroups.find((gr) => gr.id === g);
    setSelectedGroup(g);
    setSelectedSegment(grp && grp.segments.length > 0 ? grp.segments[0].value : '');
  }

  React.useEffect(() => {
    if (grade === 10 && selectedGroup === 'stream') handleGroupChange('overall');
  }, [grade]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (year: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('year', String(year));
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('class', tab);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const {
    data: kvDetailsData,
    isFetching: isKvDetailsLoading,
    isError: isKvDetailsError,
  } = useGetResultKvDetailsQuery(
    kvCode ? { grade, year: selectedYear, kvCode } : skipToken,
  );

  const {
    data: kvStudentDetailsData,
    isFetching: isKvStudentsLoading,
    isError: isKvStudentsError,
  } = useGetResultKvStudentResultDetailsQuery(
    kvCode ? { grade, year: selectedYear, kvCode } : skipToken,
  );

  const schoolSummary = kvDetailsData?.summary || null;
  const isPmShriSchool = useMemo(
    () => (kvDetailsData?.pmshri || []).some((item) => Boolean(item.isPmshri) && toNumber(item.totalRegistered) > 0),
    [kvDetailsData],
  );

  const students = useMemo<StudentRecord[]>(() => {
    if (!kvStudentDetailsData || !schoolSummary) return [];
    const region = sanitizeLabel(schoolSummary.region);
    const schoolName = sanitizeLabel(schoolSummary.schoolName);
    const kvCodeLabel = sanitizeLabel(schoolSummary.kvCode);
    return kvStudentDetailsData.flatMap((student) => {
      const rollNo = sanitizeLabel(student.rollNo);
      if (!rollNo) return [];
      const subjects = (student.subjects || []).flatMap((subject) => {
        const subjectName = sanitizeLabel(subject.subjectName);
        const subjectCode = sanitizeLabel(subject.subjectCode);
        if (!subjectName || !subjectCode) return [];
        const theory = toNumber(subject.theoryMarks);
        const practical = toNumber(subject.practicalMarks);
        const internal = toNumber(subject.internalMarks);
        return [{ code: subjectCode, name: subjectName, theory, practical, total: theory + practical + internal, grade: sanitizeLabel(subject.grade).toUpperCase() || 'NA' }];
      });
      const calculatedTotal = subjects.reduce((sum, s) => sum + s.total, 0);
      const totalMarks = toNumber(student.totalMarks) || calculatedTotal;
      return [{
        rollNo,
        name: sanitizeLabel(student.studentName) || `Student ${rollNo}`,
        fatherName: sanitizeLabel(student.fatherName) || 'N/A',
        motherName: sanitizeLabel(student.motherName) || 'N/A',
        dob: '',
        gender: sanitizeLabel(student.gender) || 'N/A',
        category: sanitizeLabel(student.socialCategory) || 'N/A',
        region, kvCode: kvCodeLabel, schoolName, state: '',
        isPmShri: isPmShriSchool,
        subjects, totalMarks,
        result: sanitizeLabel(student.result).toUpperCase() || 'UNKNOWN',
        session: String(selectedYear),
      }];
    });
  }, [kvStudentDetailsData, schoolSummary, isPmShriSchool, selectedYear]);

  const subjectRows = kvDetailsData?.subjects || [];
  const streamRows = kvDetailsData?.streams || [];
  const useStreamRows = grade === 12 && streamRows.length > 0;

  const schoolSubjects = useMemo(
    () => mapRowsToSubjects(
      useStreamRows ? streamRows : subjectRows,
      (item) => (useStreamRows ? item.streamLabel || item.stream : item.subject || item.streamLabel || item.stream),
    ).sort((a, b) => b.totalAppeared - a.totalAppeared),
    [useStreamRows, streamRows, subjectRows],
  );

  // Category-wise EntityRows
  const comparisonRows = useMemo<EntityRow[]>(() => {
    switch (selectedGroup) {
      case 'gender': return mapKvCompToRows(kvDetailsData?.gender || [], (i) => i.gender || i.label);
      case 'socialcategory': return mapKvCompToRows(kvDetailsData?.category || [], (i) => i.socialCategory || i.label);
      case 'location': return mapKvCompToRows(kvDetailsData?.urbanRural || [], (i) => i.locationType || i.label);
      case 'cwsn': return mapKvCompToRows(kvDetailsData?.cwsn || [], (i) => i.label);
      case 'pmshri': return mapKvCompToRows(kvDetailsData?.pmshri || [], (i) => i.label);
      case 'sponsoredby':
      case 'sector': return mapKvCompToRows(kvDetailsData?.sponsoredBy || [], (i) => i.sector || i.label);
      case 'stream':
        return streamRows.flatMap((item) => {
          const name = sanitizeLabel(item.streamLabel || item.stream);
          if (!name) return [];
          return [{
            name,
            passPercentage: toNumber(item.passPercentage),
            performanceIndex: toNumber(item.performanceIndex),
            totalRegistered: toNumber(item.appearedStudents),
            totalAppeared: toNumber(item.appearedStudents),
            totalPassed: toNumber(item.passed ?? 0),
            totalCompartment: 0,
            totalAbsent: 0,
          }];
        }).filter((r) => r.totalAppeared > 0);
      default: return [];
    }
  }, [selectedGroup, kvDetailsData, streamRows]);

  const { maleCount, femaleCount } = useMemo(() => {
    const counts = { maleCount: 0, femaleCount: 0 };
    for (const item of kvDetailsData?.gender || []) {
      const label = sanitizeLabel(item.gender || item.label).toLowerCase();
      if (label === 'male') counts.maleCount = toNumber(item.totalRegistered);
      else if (label === 'female') counts.femaleCount = toNumber(item.totalRegistered);
    }
    return counts;
  }, [kvDetailsData]);

  const displayStudents = useMemo(() => {
    let list = [...students];
    // Filter by selected classification segment
    if (selectedGroup === 'gender' && selectedSegment) {
      list = list.filter((s) => s.gender.toLowerCase() === selectedSegment.toLowerCase());
    } else if (selectedGroup === 'socialcategory' && selectedSegment) {
      const SOCIAL_CAT_KEYWORDS: Record<string, string[]> = {
        general: ['general', 'unreserved', 'ur'],
        obc: ['obc', 'other backward'],
        sc: ['sc', 'scheduled caste'],
        st: ['st', 'scheduled tribe'],
      };
      const keywords = SOCIAL_CAT_KEYWORDS[selectedSegment.toLowerCase()] ?? [selectedSegment.toLowerCase()];
      list = list.filter((s) => {
        const cat = s.category.toLowerCase();
        return keywords.some((kw) => cat === kw || cat.startsWith(kw) || cat.includes(kw));
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q) || s.rollNo.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sortKey === 'name') return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      if (sortKey === 'totalMarks') return sortAsc ? a.totalMarks - b.totalMarks : b.totalMarks - a.totalMarks;
      if (sortKey === 'result') return sortAsc ? a.result.localeCompare(b.result) : b.result.localeCompare(a.result);
      return 0;
    });
    return list;
  }, [students, search, sortKey, sortAsc, selectedGroup, selectedSegment]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const displaySegmentLabel = useMemo(() => {
    if (selectedGroup === 'overall') return 'Overall';
    const seg = activeGroupDef.segments.find((s) => s.value === selectedSegment);
    return seg ? activeGroupDef.label + ' — ' + seg.label : activeGroupDef.label;
  }, [selectedGroup, selectedSegment, activeGroupDef]);

  // ── Early returns ─────────────────────────────────────────────────────────
  if (!kvCode) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Invalid KV code.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }
  if (isKvDetailsLoading && !schoolSummary) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500">Loading school summary...</div>
      </div>
    );
  }
  if (!schoolSummary || isKvDetailsError) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">School with KV code "{kvCode}" not found.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  const schoolRegion = sanitizeLabel(schoolSummary.region) || decodedRegion || 'Unknown';
  const genderRatioLabel = maleCount > 0 || femaleCount > 0 ? `${maleCount}M / ${femaleCount}F` : 'N/A';
  const cards = [
    { title: 'Total Students', value: schoolSummary.totalRegistered, subtitle: `Class ${grade}`, icon: <Users className="w-5 h-5" />, color: 'purple' as const },
    { title: 'Pass %', value: `${schoolSummary.passPercentage}%`, subtitle: `${schoolSummary.totalPassed} passed`, icon: <CheckCircle2 className="w-5 h-5" />, color: 'green' as const },
    { title: 'Compartment', value: schoolSummary.totalCompartment, subtitle: 'Students', icon: <BookOpen className="w-5 h-5" />, color: 'orange' as const },
    { title: 'Perf. Index', value: schoolSummary.performanceIndex, subtitle: 'Performance Index', icon: <Award className="w-5 h-5" />, color: 'blue' as const },
    { title: 'Ess. Repeat', value: schoolSummary.totalEssentialRepeat, subtitle: 'Students', icon: <XCircle className="w-5 h-5" />, color: 'red' as const },
    { title: 'Gender Ratio', value: genderRatioLabel, subtitle: 'Male / Female', icon: <TrendingUp className="w-5 h-5" />, color: 'pink' as const },
  ];

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase cursor-pointer hover:text-[#F36B21] select-none"
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortKey === k ? 'text-[#F36B21]' : 'text-gray-300'} />
      </span>
    </th>
  );

  const subjectChartTitle = useStreamRows
    ? `Stream-wise Performance — ${schoolSummary.schoolName}`
    : `Subject-wise Performance — ${schoolSummary.schoolName}`;
  const noStudentsMessage = isKvStudentsLoading
    ? 'Loading student records...'
    : 'Student-level records are not available for this school/year.';
  const isOverall = selectedGroup === 'overall';

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      {isKvDetailsLoading && (
        <div className="text-sm text-gray-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">Refreshing school data...</div>
      )}
      {isKvStudentsError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">Unable to load student-level records right now.</div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('..')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 truncate">{schoolSummary.schoolName}</h1>
            {isPmShriSchool && (
              <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold ring-1 ring-blue-300">PM SHRI</span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            KV Code: {schoolSummary.kvCode} — {schoolRegion} Region — {schoolSummary.totalRegistered} students
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 inline-flex overflow-hidden">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id ? 'text-[#F36B21] border-[#F36B21] bg-orange-50' : 'text-gray-600 border-transparent hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <ResultYearDropdown grade={grade} value={selectedYear} onChange={handleYearChange} />
        </div>
      </div>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <StatCard key={c.title} title={c.title} value={c.value} subtitle={c.subtitle} icon={c.icon} color={c.color} />
        ))}
      </div>

      {/* ── Filter panel ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">

        {/* View */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">View</span>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        {/* Classification */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Classification</span>
          <div className="flex flex-wrap gap-2">
            {allGroups.map((g) => (
              <Chip key={g.id} label={g.label} active={selectedGroup === g.id} onClick={() => handleGroupChange(g.id)} />
            ))}
          </div>
        </div>

        {/* Segment sub-row */}
        {activeGroupDef.segments.length > 0 && (
          <div className="px-6 py-2.5 flex flex-wrap items-center gap-3 bg-gray-50/60">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Segment</span>
            <div className="flex flex-wrap gap-2">
              {activeGroupDef.segments.map((seg) => (
                <Chip key={seg.value} label={seg.label} active={selectedSegment === seg.value} onClick={() => setSelectedSegment(seg.value)} />
              ))}
            </div>
          </div>
        )}

        {/* Metric */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Metric</span>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {METRIC_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMetricMode(m.id)}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  metricMode === m.id ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Active filter badge ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Showing:</span>
        <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 font-semibold">Class {grade}</span>
        <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">{selectedYear}</span>
        <span className="px-2 py-0.5 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold">{displaySegmentLabel}</span>
        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-gray-700 font-semibold">
          {METRIC_MODES.find((m) => m.id === metricMode)?.label}
        </span>
      </div>

      {/* ── Overall: Subject chart ─────────────────────────────────────────── */}
      {isOverall && <SubjectPerformanceChart data={schoolSubjects} title={subjectChartTitle} />}

      {/* ── Category classification ───────────────────────────────────────── */}
      {!isOverall && (
        <>
          {viewMode === 'graphical' && (
            <div>
              <SectionLabel>{displaySegmentLabel} — Breakdown</SectionLabel>
              {comparisonRows.length > 0 ? (
                <EntityBarChart
                  data={comparisonRows}
                  metric={metric}
                  title={`${displaySegmentLabel} — ${schoolSummary.schoolName}`}
                />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                  No data available for this classification.
                </div>
              )}
            </div>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>{displaySegmentLabel} — Tabular Breakdown</SectionLabel>
              <div className="bg-white rounded-xl border border-gray-200 overflow-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="py-3 px-4 font-semibold text-gray-600">{activeGroupDef.label}</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Appeared</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Passed</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Compartment</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Pass %</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Perf. Index</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {comparisonRows.length > 0 ? comparisonRows.map((row) => (
                      <tr key={row.name} className="hover:bg-orange-50 transition-colors">
                        <td className="py-3 px-4 font-medium text-gray-800">{row.name}</td>
                        <td className="py-3 px-4 text-gray-700">{row.totalAppeared.toLocaleString()}</td>
                        <td className="py-3 px-4 text-green-700 font-medium">{row.totalPassed.toLocaleString()}</td>
                        <td className="py-3 px-4 text-orange-600">{row.totalCompartment}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-sm font-semibold ${
                            row.passPercentage >= schoolSummary.passPercentage ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {row.passPercentage}%
                          </span>
                        </td>
                        <td className={`py-3 px-4 font-medium ${
                          row.performanceIndex >= schoolSummary.performanceIndex ? 'text-green-700' : 'text-red-600'
                        }`}>
                          {row.performanceIndex}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="py-8 text-center text-gray-400">No data available.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Student Table ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Students — Class {grade}</h3>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={students.length > 0 ? 'Search student...' : 'Student data unavailable'}
              disabled={students.length === 0}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 w-56 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">Roll No</th>
                <SortHeader label="Student Name" k="name" />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">Gender</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">Category</th>
                <SortHeader label="Total Marks" k="totalMarks" />
                <SortHeader label="Result" k="result" />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">Subjects</th>
              </tr>
            </thead>
            <tbody>
              {displayStudents.map((s) => (
                <tr
                  key={s.rollNo}
                  className="border-b border-gray-100 hover:bg-orange-50/40 cursor-pointer transition"
                  onClick={() => navigate(`student/${s.rollNo}?class=${activeTab}&year=${selectedYear}`)}
                >
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{s.rollNo}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{s.name}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.gender}</td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{s.totalMarks}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      s.result === 'PASS' ? 'bg-green-50 text-green-700' :
                      s.result === 'COMPTT' ? 'bg-orange-50 text-orange-700' :
                      'bg-red-50 text-red-700'
                    }`}>{s.result}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.subjects.length} subjects</td>
                </tr>
              ))}
              {displayStudents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">{noStudentsMessage}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}